/**
 * Coach Engine — Public API
 *
 * Single entry point used by every UI surface:
 *
 *   import { coach } from 'services/coach';
 *
 *   const observations = coach.analyze(
 *     { type: 'session', id: session.id },
 *     { session, trades, allTrades, ...ctx }
 *   );
 *
 *   const text = coach.narrate(observations);  // plain narrator
 *
 * Scopes currently supported:
 *   - 'session'  — per-group insights (trades sharing a sessionId)
 *   - 'progress' — cross-session trends, recurrences, resolutions, playbook
 *                  leaks. Takes { sessions, playbooks, currentTradingDay,
 *                  windowDays? } and returns ranked progress observations.
 *   - 'day'      — daily review: grade + performance + mistakes + setup/ATM +
 *                  time-of-day + duration + overtrading + baseline + news/events.
 *                  Also exposed via `coach.reviewDay(date, ctx)` which returns
 *                  { observations, grade, day } for richer surfaces.
 *
 * Planned (Phase 2+):
 *   - 'trade'    — single-trade commentary
 *   - 'account'  — per-account drift
 *   - 'global'   — top-level dashboard insights
 *   - 'live'     — in-progress trade coaching (Live Coach)
 */

import { analyzeSession } from './scopes/session';
import { analyzeProgress } from './scopes/progress';
import { analyzeDay } from './scopes/day';
import { analyzeSetup } from './scopes/setup';
import { analyzeAtm } from './scopes/atm';
import { analyzeAccount } from './scopes/account';
import { analyzeTrade } from './scopes/trade';
import plainNarrator from './narrators/plain';

// Tier constants — consumed by UI gating. 'free' covers everything that
// existed in the legacy Daily Review; 'pro' is reserved for the deeper
// analyzers + LLM narrator being added next.
export const TIER = Object.freeze({ FREE: 'free', PRO: 'pro' });

// Re-export primitives so callers don't need to dig into subpaths.
export {
  SEVERITY, SEVERITY_STYLE, CATEGORY, SCOPE,
  ACTION_TYPE, makeObservation, makeAction
} from './types';
export {
  scoreObservation, rankObservations, dedupeObservations, topObservations
} from './priorities';
export { buildSessionSignature } from './utils';

// Day-scope helpers — the legacy-shape adapter is exported so the existing
// Daily Review modal can keep rendering via the Coach pipeline without
// touching 600+ lines of JSX.
export { toLegacyReviewShape } from './scopes/day';

// Observation muting — keeps the Coach quiet on patterns the trader has
// already acted on. State lives in TradingJournal (settings.json), filter
// + enrich helpers live in the engine for shared use.
export {
  filterMuted,
  enrichWithMuteAction,
  cleanExpiredMutes,
  muteExpiry,
} from './muting';

// Observation Archive (Phase 2B-3) — pure query/aggregation helpers over the
// corpus of per-session aiInsight.observations. No duplicate storage; the
// archive is a derived view that works retroactively on every session you've
// already journaled. Consumed by the progress scope (2B-4), the Report Card
// UI (2B-5), day/week rollups (2B-6), and recurrence badges (2B-7).
export {
  flattenObservations,
  queryObservations,
  countByAnalyzer,
  countByPlaybook,
  countBySeverity,
  groupByWeek,
  groupByDay,
  recurrenceInfo,
  recurrenceAll,
  countSessions
} from './archive';

/**
 * Unified analyze entry. Dispatches to the correct scope.
 *
 * @param {Object} scope           { type, id? }
 * @param {Object} context         Scope-specific context (see each scope's signature)
 * @returns {Observation[]}
 */
const analyze = (scope, context = {}) => {
  if (!scope || !scope.type) return [];

  switch (scope.type) {
    case 'session': {
      // The session object is either passed directly in `context.session`
      // or looked up from `context.sessions` by scope.id.
      const session = context.session
        || (Array.isArray(context.sessions)
              ? context.sessions.find(s => s.id === scope.id)
              : null);
      if (!session) return [];
      return analyzeSession({ ...context, session });
    }

    case 'progress': {
      // Cross-session scope. Accepts { sessions, playbooks, currentTradingDay }
      // in context; windowDays can come from either the scope descriptor
      // (e.g. { type: 'progress', windowDays: 90 }) or context. Scope wins.
      return analyzeProgress({
        ...context,
        windowDays: scope.windowDays ?? context.windowDays ?? 30
      });
    }

    case 'day': {
      // Day scope. scope.id is 'YYYY-MM-DD'. Context expects:
      //   trades, allTrades, journalEntry, allJournalEntries, accounts,
      //   getTradeNetPnL, getAccountById, tier
      // Returns just the observations[] to match the analyze contract.
      // Callers needing grade/day context should use `coach.reviewDay` below.
      const result = analyzeDay({ ...context, date: scope.id });
      return result.observations;
    }

    case 'setup': {
      // Setup scope. scope.id is the setup name (e.g. "MMXM Setup").
      // Context expects: allTrades, anchorDate (today's trading day), tier.
      return analyzeSetup({ ...context, setupName: scope.id });
    }

    case 'atm': {
      // ATM scope. scope.id is the ATM strategy name (matched against
      // trade.atmStrategy). Context expects: allTrades, anchorDate, tier.
      return analyzeAtm({ ...context, atmName: scope.id });
    }

    case 'account': {
      // Account scope. scope.id is the accountId (matched against
      // trade.accountId). Context expects: allTrades + getAccountById +
      // optional accountLabel for display. Analyzers compare this account
      // to baseline + cross-account peer behavior on the same setups.
      return analyzeAccount({
        ...context,
        accountId: scope.id,
        accountLabel: scope.label || context.accountLabel,
      });
    }

    case 'trade': {
      // Per-trade scope. scope.id is the trade id; analyzers narrate THIS
      // trade specifically. Context provides the full trade object via
      // context.trade (or looks it up from context.allTrades by scope.id).
      const tradeObj = context.trade
        || (Array.isArray(context.allTrades)
              ? context.allTrades.find(t => t && t.id === scope.id)
              : null);
      if (!tradeObj) return [];
      return analyzeTrade({ ...context, trade: tradeObj });
    }

    // Future: account, global, live
    default:
      console.warn(`[Coach] unknown scope type: ${scope.type}`);
      return [];
  }
};

/**
 * Day-review convenience wrapper. Returns the richer shape the Daily Review
 * modal (and the LiveCoachFeed's DAILY section) need — observations PLUS the
 * grade object PLUS the raw DayContext for the stats grid.
 *
 * Use this when you need both the ranked coach signals AND the numeric stats
 * that traditionally lived inside `generateDailyReview`. For pure signal
 * consumption, `coach.analyze({type:'day',id:date}, ctx)` is enough.
 *
 * @param {string} date  YYYY-MM-DD
 * @param {Object} context  Same shape as analyze() context for day scope
 * @returns {{ observations, grade, day }}
 */
const reviewDay = (date, context = {}) => {
  return analyzeDay({ ...context, date });
};

/**
 * Grade a day without running the full analyzer bank. Thin wrapper around
 * `computeGrade(buildDayContext(...))` for surfaces that only need the letter.
 */
const gradeDay = (date, context = {}) => {
  const result = analyzeDay({ ...context, date, top: 0 });
  return result.grade;
};

/**
 * Turn observations into a single string (legacy text slot).
 * UI surfaces that want rich rendering should consume observations directly.
 *
 * @param {Observation[]} observations
 * @returns {string}
 */
const narrate = (observations) => plainNarrator.renderAll(observations);

export const coach = {
  analyze,
  narrate,
  reviewDay,
  gradeDay
};

export default coach;
